import React, { useEffect, useState, useRef } from 'react';
import DataTable from '../common/DataTable';
import { numOfPage, ShippedLabelHistoryColumns } from '../../constant';
import { useDispatch, useSelector } from 'react-redux';
import BatchPagination from '../batch/BatchPagination';
import { getShippedLabelHistory, updatePickedItemStatus, incrementPickedQuantity } from '../../store/actions/picker.action';
import moment from 'moment';
import DatePickerComponent from "../common/DatePickerComponent";
import LoaderSpinner from '../common/Loader';
import _ from 'lodash';
import { toast } from "react-toastify";
import { commaSeparatedStringToArray, useIsMobile } from '../../config/utils';
import { getmergePdfForPickingPdf } from '../../store/actions/batch.action';
import Modal from 'react-bootstrap/Modal';
import ImageSlider from '../incoming/ImageSlider';
import noImage from '../../assets/No-Image-Placeholder.png';
import MobilePickPlan from './MobilePickPlan';
import './PickScanDialog.css';

/*istanbul ignore next */
const ShippedLabelHistory = () => {
    let dispatch = useDispatch();
    const [loader, setLoader] = useState(false);
    const [packingLabels, setPackingLabels] = useState([]);
    const [scanInput, setScanInput] = useState('');
    const [scanFeedback, setScanFeedback] = useState(null);
    const [activeTab, setActiveTab] = useState('pending');
    const [previewItem, setPreviewItem] = useState(null);
    const scanInputRef = useRef(null);
    const scanTimerRef = useRef(null);
    const isMobile = useIsMobile(768);
    const { shippedLabelHistoryStatus, shippedLabelHistory } = useSelector((state) => state.pickerReducer);

    // Keep scan input always focused — barcode scanner needs it on both
    // desktop and mobile. Skip refocus when the user is interacting with
    // another form control (input/textarea/select/button) or inside a modal,
    // so taps on cards, tab buttons, checkboxes and dialogs still work.
    useEffect(() => {
        const keepFocus = () => {
            const el = scanInputRef.current;
            if (!el) return;
            const active = document.activeElement;
            if (active === el) return;
            if (active && active !== document.body) {
                const tag = (active.tagName || '').toUpperCase();
                if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag)) return;
                if (typeof active.closest === 'function' && active.closest('.modal, [role="dialog"]')) return;
            }
            el.focus();
        };
        keepFocus();
        const interval = setInterval(keepFocus, 500);
        document.addEventListener('click', keepFocus);
        return () => {
            clearInterval(interval);
            document.removeEventListener('click', keepFocus);
        };
    }, [isMobile]);

    let dataLoad = {
        pageIndex: 1,
        pageSize: 30,
        sortColumn: '',
        sortOrder: 'ASCENDING',
        searchkey: '',
        startDate: moment().format('YYYY-MM-DDT00:00:00'),
        endDate: moment().format('YYYY-MM-DDT00:00:00'),
    }
    const [filterData, setfilterData] = useState(dataLoad);

    const playSound = (type) => {
        try {
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioCtx.destination);
            gainNode.gain.value = 0.3;
            if (type === 'success') {
                oscillator.frequency.value = 880;
                oscillator.type = 'sine';
            } else {
                oscillator.frequency.value = 300;
                oscillator.type = 'square';
            }
            oscillator.start();
            oscillator.stop(audioCtx.currentTime + 0.15);
        } catch (e) {
            // Audio not supported
        }
    };

    const processScan = async (rawValue) => {
        const scannedValue = (rawValue || '').trim();
        if (!scannedValue) return;

        // Find matching row by SKU
        const matchingItem = packingLabels.find(
            (item) => item.ProductSKU && item.ProductSKU.toLowerCase() === scannedValue.toLowerCase()
        );

        if (!matchingItem) {
            setScanFeedback({ type: 'error', message: `SKU "${scannedValue}" not found in pick list` });
            playSound('error');
            setScanInput('');
            setTimeout(() => setScanFeedback(null), 3000);
            return;
        }

        if ((matchingItem.PickedQuantity || 0) >= matchingItem.TotalQuantity) {
            setScanFeedback({ type: 'warning', message: `SKU "${scannedValue}" already fully picked` });
            playSound('error');
            setScanInput('');
            setTimeout(() => setScanFeedback(null), 3000);
            return;
        }

        // Pick ONE UNPICKED item per scan. UnpickedOutbounditemIdList comes from the
        // SP containing only IsPicked=0 rows, so the first ID is always unpicked.
        const unpickedIds = (matchingItem.UnpickedOutbounditemIdList || '').split(',').map(s => s.trim()).filter(Boolean);
        if (!unpickedIds.length) {
            setScanFeedback({ type: 'warning', message: `SKU "${scannedValue}" has no unpicked items remaining` });
            playSound('error');
            setScanInput('');
            setTimeout(() => setScanFeedback(null), 3000);
            return;
        }

        let payloadData = {
            commaSeparatedIds: unpickedIds[0]
        };

        setLoader(true);
        try {
            let updateRes = await incrementPickedQuantity(payloadData);
            if (updateRes?.data?.Status === 200) {
                setScanFeedback({
                    type: 'success',
                    message: `${matchingItem.ProductSKU} - Picked! (${matchingItem.CompanySellerName})`
                });
                playSound('success');
                fetchAllList();
            } else {
                setScanFeedback({ type: 'error', message: 'Failed to update pick status' });
                playSound('error');
            }
        } catch (err) {
            console.error('Error updating pick status:', err);
            setScanFeedback({ type: 'error', message: 'Error updating pick status' });
            playSound('error');
        }
        setLoader(false);
        setScanInput('');
        setTimeout(() => setScanFeedback(null), 3000);
    };

    const handleAction = async (item, type) => {
        if (type === 'productPreview') {
            setPreviewItem(item);
            return;
        }
        let data = commaSeparatedStringToArray(item.OutbounditemIdList);
        let payloadData = {
            commaSeparatedIds: item.OutbounditemIdList
        }
        if (data.length) {
            let updateRes = await updatePickedItemStatus(payloadData);
            if (updateRes?.data?.Status === 200) {
                toast.success('Status Updated Successfully');
                fetchAllList();
            }
        } else {
            toast.error('Failed to update status');
        }
    };

    // Mobile tap-to-pick: optimistic local update + single-item increment.
    // Reverts on API failure so progress bar/tab badges stay truthful.
    const pickItem = async (item) => {
        const unpickedIds = (item.UnpickedOutbounditemIdList || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        if (!unpickedIds.length) {
            toast.warn(`SKU "${item.ProductSKU}" has no unpicked items remaining`);
            return;
        }
        // Optimistic update
        setPackingLabels((prev) =>
            prev.map((row) =>
                row.listIndex === item.listIndex
                    ? {
                          ...row,
                          PickedQuantity: (row.PickedQuantity || 0) + 1,
                          QtyDisplay: `${(row.PickedQuantity || 0) + 1} / ${row.TotalQuantity}`,
                      }
                    : row
            )
        );
        try {
            const res = await incrementPickedQuantity({ commaSeparatedIds: unpickedIds[0] });
            if (res?.data?.Status === 200) {
                fetchAllList();
            } else {
                // Revert
                setPackingLabels((prev) =>
                    prev.map((row) =>
                        row.listIndex === item.listIndex
                            ? {
                                  ...row,
                                  PickedQuantity: Math.max((row.PickedQuantity || 1) - 1, 0),
                                  QtyDisplay: `${Math.max((row.PickedQuantity || 1) - 1, 0)} / ${row.TotalQuantity}`,
                              }
                            : row
                    )
                );
                toast.error('Failed to update pick status');
            }
        } catch (err) {
            console.error('Error updating pick status:', err);
            setPackingLabels((prev) =>
                prev.map((row) =>
                    row.listIndex === item.listIndex
                        ? {
                              ...row,
                              PickedQuantity: Math.max((row.PickedQuantity || 1) - 1, 0),
                              QtyDisplay: `${Math.max((row.PickedQuantity || 1) - 1, 0)} / ${row.TotalQuantity}`,
                          }
                        : row
                )
            );
            toast.error('Error updating pick status');
        }
    };

    const handleSortingColumn = (key) => {
        let newSortOrder = filterData.sortOrder === "ASCENDING" ? "DESCENDING" : "ASCENDING";
        setfilterData({
            ...filterData,
            sortColumn: key,
            sortOrder: newSortOrder,
        });
    };

    useEffect(() => {
        if (shippedLabelHistoryStatus === 'PENDING') {
            setLoader(true);
        } else if (shippedLabelHistoryStatus === 'SUCCESS') {
            setLoader(false);
        }
    }, [shippedLabelHistoryStatus]);

    useEffect(() => {
        fetchAllList();
    }, [filterData.numberOfPage, filterData.pageIndex, filterData.pageSize, filterData.sortColumn, filterData.sortOrder]);

    const fetchAllList = () => {
        let payload = {
            ...filterData,
            startDate: moment(filterData.startDate).format('YYYY-MM-DD'),
            endDate: moment(filterData.endDate).format('YYYY-MM-DD')
        };
        dispatch(getShippedLabelHistory(payload));
    }

    const handleDateChange = (date) => {
        let dateFormat = moment(date).format('YYYY-MM-DDT00:00:00');
        let dataLoad = {
            ...filterData,
            startDate: dateFormat,
            endDate: dateFormat
        }
        setfilterData(dataLoad);
        let payload = {
            ...dataLoad,
            startDate: moment(date).format('YYYY-MM-DD'),
            endDate: moment(date).format('YYYY-MM-DD')
        }
        dispatch(getShippedLabelHistory(payload));
    }

    useEffect(() => {
        if (shippedLabelHistory?.shippedLabelHistory) {
            let addlistIndex = shippedLabelHistory.shippedLabelHistory.map((item, index) => ({
                ...item,
                listIndex: index,
                isChecked: false,
                QtyDisplay: `${item.PickedQuantity || 0} / ${item.TotalQuantity}`,
            }));
            setPackingLabels(addlistIndex);
            if (shippedLabelHistoryStatus === 'success') {
                setfilterData({
                    ...filterData,
                    totalPages: Math.ceil(shippedLabelHistory?.TotalRecords / filterData.pageSize),
                });
            }
        }
    }, [shippedLabelHistory]);

    const handleCheckRecord = (e, type) => {
        const { checked } = e.target;
        let recordData = [...packingLabels];
        if (_.isEmpty(type)) {
            if (checked) {
                let markCheck = recordData.map((item) => ({
                    ...item,
                    isChecked: true,
                }));
                setPackingLabels(markCheck);

            } else {
                let markCheck = recordData.map((item) => ({
                    ...item,
                    isChecked: false,
                }));
                setPackingLabels(markCheck);
            }
        } else {
            recordData[type.listIndex].isChecked = checked;
            setPackingLabels(recordData);
        }
    };

    const handleMultiUpdate = async () => {
        let checkedData = packingLabels.filter((item) => {
            if (item?.isChecked) {
                return item;
            }
        });
        if (checkedData.length) {
            const outboundIds = checkedData.map(item => item.OutbounditemIdList).join(',');
            let payloadData = {
                commaSeparatedIds: outboundIds
            }
            let updateRes = await updatePickedItemStatus(payloadData);
            if (updateRes?.data?.Status === 200) {
                toast.success('Status Updated Successfully');
                fetchAllList();
            }
        } else {
            toast.error('Failed to update status');
        }
    };

    const handleSectionPrintAllAction = async (event) => {

        event.stopPropagation();
        let checkedData = packingLabels.filter((item) => {
            if (item?.isChecked) {
                return item;
            }
        });

        if (!checkedData.length) {
            toast.error('No orders selected for printing');
            return;
        }

        const orderNumbers = checkedData.map(order => order?.orderNumber).join(',');
        if (!orderNumbers) {
            toast.error('Could not retrieve order numbers for selected orders');
            return;
        }
        const data = commaSeparatedStringToArray(orderNumbers) || null;

        if (data.length) {
            let payload = {
                listOrderNumber: data,
                endDate: filterData.endDate,
                startDate: filterData.startDate,
            };
            setLoader(true);
            const mergedRes = await getmergePdfForPickingPdf(payload);
            if (mergedRes?.data?.Status === 200) {
                window.open(mergedRes.data.Entity);
                toast.success('Picklist Printed Successfully');
                fetchAllList();
                setLoader(false);
            }
        } else {
            toast.error('Could not process selected orders');
            setLoader(false);
        }
    }

    return (
        <>
        {isMobile ? (
            <MobilePickPlan
                packingLabels={packingLabels}
                scanInput={scanInput}
                setScanInput={setScanInput}
                scanInputRef={scanInputRef}
                scanTimerRef={scanTimerRef}
                scanFeedback={scanFeedback}
                processScan={processScan}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                handleCheckRecord={handleCheckRecord}
                handleMultiUpdate={handleMultiUpdate}
                handleSectionPrintAllAction={handleSectionPrintAllAction}
                filterData={filterData}
                handleDateChange={handleDateChange}
                pickItem={pickItem}
                onPreviewItem={(item) => setPreviewItem(item)}
            />
        ) : (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    Order Picking Plan
                    <div className="pickHead mb-3 pl-3 pr-3 pt-3 zIndex2">
                        <div></div>
                        <div className="pickHead">
                            <div className="fontFix">
                                <DatePickerComponent
                                    dateField={filterData.startDate}
                                    handleDateChange={(date) => handleDateChange(date)}
                                    fieldName="ValidFrom"
                                    dateFormat="MM-dd-yyyy"
                                    showFutureDate={false}
                                />
                            </div>

                            <button disabled={!packingLabels.filter(item => item.isChecked).length} className="btn cus-seconday-bg-btn showHideFilter ml-2" onClick={handleMultiUpdate}>
                                <i className="fa fa-print" aria-hidden="true"></i> Marked As Picked
                            </button>

                            <button disabled={!packingLabels.filter(item => item.isChecked).length} className="btn cus-seconday-bg-btn showHideFilter ml-2" onClick={(event) => handleSectionPrintAllAction(event)}>
                                <i className="fa fa-list-alt" aria-hidden="true"></i> Print Picklist
                            </button>
                        </div>
                    </div>
                </div>

                {/* Global Scan - auto-focused input fires processScan after 150ms of no keystrokes */}
                <div className="pick-scan-bar mb-3 ml-2 mr-2">
                    <div className="pick-scan-bar-inner">
                        <i className="fa fa-barcode pick-scan-bar-icon"></i>
                        <input
                            ref={scanInputRef}
                            type="text"
                            className="pick-scan-bar-input"
                            value={scanInput}
                            onChange={(e) => {
                                const val = e.target.value;
                                setScanInput(val);
                                if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
                                if (val.trim()) {
                                    scanTimerRef.current = setTimeout(() => processScan(val), 150);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
                                    processScan(scanInput);
                                }
                            }}
                            placeholder="Ready to scan..."
                            autoComplete="off"
                            autoFocus
                        />
                    </div>
                </div>
                {scanFeedback && (
                    <div className={`pick-scan-feedback pick-scan-${scanFeedback.type} mb-3 ml-2 mr-2`}>
                        <span className="pick-scan-icon">
                            {scanFeedback.type === 'success' && <i className="fa fa-check-circle"></i>}
                            {scanFeedback.type === 'error' && <i className="fa fa-times-circle"></i>}
                            {scanFeedback.type === 'warning' && <i className="fa fa-exclamation-triangle"></i>}
                        </span>
                        <span className="pick-scan-message">{scanFeedback.message}</span>
                    </div>
                )}

                {loader && (<div className="full-screen-spinner-overlay"><LoaderSpinner data-testid="loader-spinner" /></div>)}

                {(() => {
                    const pendingRows = packingLabels.filter(i => (i.PickedQuantity || 0) < i.TotalQuantity);
                    const pickedRows = packingLabels.filter(i => (i.PickedQuantity || 0) >= i.TotalQuantity);
                    const visibleRows = activeTab === 'pending' ? pendingRows : pickedRows;
                    const pendingQty = pendingRows.reduce((sum, i) => sum + i.TotalQuantity - (i.PickedQuantity || 0), 0);
                    const pickedQty = pickedRows.reduce((sum, i) => sum + (i.PickedQuantity || 0), 0);
                    return (
                        <>
                            <ul className="nav nav-tabs pick-tabs ml-2 mr-2 mb-2">
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link ${activeTab === 'pending' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('pending')}
                                    >
                                        Pending <span className="badge badge-secondary ml-1">{pendingQty}</span>
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        type="button"
                                        className={`nav-link ${activeTab === 'scanned' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('scanned')}
                                    >
                                        Picked <span className="badge badge-success ml-1">{pickedQty}</span>
                                    </button>
                                </li>
                            </ul>

                            <div className="main_content_table_history shippedLabelHistoryTable">
                                <div className="tableFixHead pl-2 zIndex1">
                                    {shippedLabelHistoryStatus === 'SUCCESS' && (
                                        <DataTable
                                            headers={ShippedLabelHistoryColumns}
                                            tableData={visibleRows}
                                            actionType={6}
                                            handleAction={handleAction}
                                            handleSortingColumn={handleSortingColumn}
                                            tableDataStatus={shippedLabelHistoryStatus}
                                            filterData={filterData}
                                            handleCheckRecord={handleCheckRecord}
                                            isSelectAll={visibleRows.length > 0 && visibleRows.every(item => item.isChecked)}
                                        />
                                    )}
                                </div>
                            </div>
                            <BatchPagination
                                gridData={visibleRows}
                                totalVisualRecords={visibleRows.length}
                                numberOfPage={filterData.pageSize}
                                numOfPage={numOfPage}
                                loader={loader}
                                currentPage={filterData.pageIndex}
                                totalPages={filterData.totalPages}
                                totalRecords={shippedLabelHistory?.TotalRecords || 0}
                                setCurrentPage={(value) => setfilterData({ ...filterData, pageIndex: value })}
                                setNumberOfPage={(value) => setfilterData({ ...filterData, pageSize: value })}
                            />
                        </>
                    );
                })()}
            </div>
        </main>
        )}

            {previewItem && (
                <Modal
                    size="xl"
                    centered
                    className="topUpModal"
                    show={!!previewItem}
                    onHide={() => setPreviewItem(null)}
                >
                    <div className="top_modal_header_container">
                        <span>{previewItem.ProductName || previewItem.ProductSKU}</span>
                        <div className="modalDeleteWrapper">
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                onClick={() => setPreviewItem(null)}
                            ></button>
                        </div>
                    </div>
                    <div className="modal_container_rate_card inlargeImageSlider">
                        {previewItem.ProductImages && previewItem.ProductImages.length > 0 ? (
                            <ImageSlider
                                isView={true}
                                isUserAdmin={false}
                                hideMaximize={true}
                                images={previewItem.ProductImages.map((url, idx) => ({
                                    id: `${previewItem.ProductSKU}-${idx}`,
                                    imageUrl: url,
                                    isPrimary: idx === 0,
                                }))}
                            />
                        ) : (
                            <div className='viewImageWrapper'>
                                <img src={noImage} alt="No product images available" />
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </>

    );
}

export default ShippedLabelHistory;
